import { useCallback } from "react";
import { QueryClient, useQueryClient } from "react-query";
import { DatatableEmployee } from "./Employees.columns";

export const EMPLOYEES_WITH_AUTH_QUERY_KEY = "employees-with-auth";

/**
 * Patches one employee in every cached row list for the shop, not just the
 * list on screen. With "Show deleted" there are two cached lists (toggle on
 * and off); patching only the visible one leaves the other showing the
 * pre-change state when the toggle is flipped.
 */
export const patchEmployeeInAllCachedLists = (
  queryClient: QueryClient,
  shopId: string | undefined,
  employeeId: string,
  patch: Partial<DatatableEmployee>
) => {
  queryClient.setQueriesData<DatatableEmployee[] | undefined>(
    [EMPLOYEES_WITH_AUTH_QUERY_KEY, shopId],
    (oldData) =>
      oldData?.map((e) => (e.id === employeeId ? { ...e, ...patch } : e))
  );
};

/**
 * The Employees table caches rows (with each account's auth status) under a
 * key built from the employee ids alone. Deleting or restoring someone moves
 * the id set away from and later back to a key that still holds their
 * pre-delete row, so call the returned function after either to drop every
 * cached id-set for the shop and refetch.
 */
export const useInvalidateEmployeesWithAuth = (shopId: string | undefined) => {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries([EMPLOYEES_WITH_AUTH_QUERY_KEY, shopId]),
    [queryClient, shopId]
  );
};
